import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { message, Spin, Button, Popconfirm, Col, Select, Row } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { isoDateToHumanReadableDate } from "../../Util";
import { getAllExceptions, reprocessPreprocessingException, getExceptionFilters } from "./api";
import ExceptionDrawer, { ExceptionStatus } from "./ExceptionDrawer"; 
import { AppContext } from "../../context/AppContext";
import * as AppPermissions from "../../Util/permissions";
import CustomList from "../components/List";
import ListItem from "../components/ListItem";
import ActionBar from "../components/ActionBar";
import CustomPagination from "../components/CustomPagination";

const ExceptionPage = () => {
  const [exceptions, setExceptions] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ councils: [], reportTypes: [], statuses: [] });
  const [filters, setFilters] = useState({ council: 'all', reportType: 'all', status: 'all' });
  const [loading, setLoading] = useState(false);
  const [selectedException, setSelectedException] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [reprocessingIds, setReprocessingIds] = useState(new Set());
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  });
  const { ensureProjects, setSelectedProject, selectProcuringEntity, setSelectedReportID, permissions } = useContext(AppContext);

  const fetchExceptions = async (page = 1, nextFilters = filters) => {
    setLoading(true);
    try {
      const response = await getAllExceptions(page, pagination.per_page, nextFilters);
      setExceptions(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Failed to fetch exceptions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExceptions(1, filters);
  }, [filters]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const res = await getExceptionFilters();
        const data = res?.data || {};
        setFilterOptions({
          councils: data.councils || [],
          reportTypes: data.report_types || data.reportTypes || [],
          statuses: data.statuses || data.status || [],
        });
      } catch (e) {
        console.warn('Failed to load exception filter options', e);
      }
    };
    loadFilters();
  }, []);

  const handleResolved = useCallback(async () => {
    const key = 'exceptions-refresh';
    message.loading({ content: 'Refreshing data…', key, duration: 0 });
    setLoading(true);
    try {
      // Clear cached selections so UI recalculates availability cleanly
      // setSelectedProject(null);
      // selectProcuringEntity(null);
      // setSelectedReportID(null);
      await ensureProjects();
      await fetchExceptions(1, filters);
      message.success({ content: 'Data refreshed', key, duration: 1.5 });
    } catch (e) {
      console.warn('Post-resolution refresh failed', e);
      message.warning({ content: 'Refresh completed with warnings', key, duration: 2 });
    } finally {
      setLoading(false);
    }
  }, [ensureProjects, fetchExceptions, filters, setSelectedProject, selectProcuringEntity, setSelectedReportID]);

  const handleReprocess = async (exceptionId) => {
    setReprocessingIds(prev => new Set(prev).add(exceptionId));
    try {
      const response = await reprocessPreprocessingException(exceptionId);
      message.success(response.message || 'Preprocessing exception reprocessing initiated successfully');
      // Refresh the exceptions list
      await fetchExceptions();
    } catch (error) {
      console.error('Failed to reprocess exception:', error);
      const errorMessage = error.response?.data?.message || 'Failed to reprocess preprocessing exception';
      message.error(errorMessage);
    } finally {
      setReprocessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(exceptionId);
        return newSet;
      });
    }
  };

  const canReprocessSubmissions = permissions?.includes(AppPermissions.CAN_REPROCESS_SUBMISSION);
  const wrapStyle = { whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4, overflow: "visible", textOverflow: "unset" , textAlign: "center"};

  // Layout sums keep total ≤24 once avatar/action columns are included.
  const description = { xxl: 14, xl: 14, lg: 14, md: 20, sm: 22, xs: 22, style: {...wrapStyle, textAlign: "left"} };
  const council = { xxl: 4, xl: 4, lg: 4, md: 5, sm: 0, xs: 0, style: wrapStyle };
  const reportType = { xxl: 3, xl: 3, lg: 3, md: 0, sm: 0, xs: 0, style: wrapStyle };
  const status = { xxl: 2, xl: 2, lg: 2, md: 2, sm: 2, xs: 2, style: wrapStyle };

  const statusColStyle = { display: "flex", alignItems: "center" };
  const statusTagStyle = (item) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "16px",
    whiteSpace: "nowrap",
    backgroundColor: item.status === ExceptionStatus.RESOLVED ? "#f6ffed" : "#fff7e6",
    color: item.status === ExceptionStatus.RESOLVED ? "#52c41a" : "#fa8c16",
    border: `1px solid ${item.status === ExceptionStatus.RESOLVED ? "#b7eb8f" : "#ffd591"}`,
  });

  const headerLayout = [
    { ...description, header: "Description" },
    { ...council, header: "Council" },
    { ...reportType, header: "Report Type" },
    { ...status, header: "Status" },
  ];

  const councilOptions = useMemo(
    () => [{ label: 'All Councils', value: 'all' }, ...(filterOptions.councils || []).map((c) => ({ label: c, value: c }))],
    [filterOptions.councils]
  );

  const reportTypeOptions = useMemo(
    () => [{ label: 'All Report Types', value: 'all' }, ...(filterOptions.reportTypes || []).map((t) => ({ label: t?.toUpperCase?.() || t, value: t }))],
    [filterOptions.reportTypes]
  );

  const statusOptions = useMemo(
    () => [{ label: 'All Statuses', value: 'all' }, ...(filterOptions.statuses || []).map((s) => ({ label: s ? s.charAt(0).toUpperCase() + s.slice(1) : s, value: s }))],
    [filterOptions.statuses]
  );

  const handleFilterChange = (key, value) => {
    // Null/undefined comes from allowClear; force sentinel 'all' for default selection.
    const nextValue = value ?? 'all';
    setFilters((prev) => ({ ...prev, [key]: nextValue }));
  };

  const onRowClick = (record) => {
    setSelectedException(record);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedException(null);
  };

  const handlePageChange = (page, pageSize) => {
    fetchExceptions(page);
  };

  return (
    <div style={{padding: "16px"}}>
      <ActionBar actionButtonProp={{
      title: "Exceptions",
     }} />
      <div style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]} justify="center" align="middle">
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Select
              allowClear
              placeholder="Filter by Council"
              options={councilOptions}
              value={filters.council}
              onChange={(value) => handleFilterChange("council", value)}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Select
              allowClear
              placeholder="Filter by Report Type"
              options={reportTypeOptions}
              value={filters.reportType}
              onChange={(value) => handleFilterChange("reportType", value)}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Select
              allowClear
              placeholder="Filter by Status"
              options={statusOptions}
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
      </div>
      <Spin spinning={loading} tip="Loading exceptions…">
        <div className="prst-table">
          <CustomList
            itemName="Exception"
            showToolbar={false}
            items={exceptions}
            loading={loading}
            headerLayout={headerLayout}
            renderListItem={({ item }) => (
              <ListItem
                key={item.id}
                name={item.name || "==//=="}
                item={item}
                isPackage={false}
                renderActions={() => {
                  if (item.type === 'preprocessing' && item.status === 'pending' && canReprocessSubmissions) {
                    return (
                      <Popconfirm
                        title="Reprocess Preprocessing Exception"
                        description="Are you sure you want to reprocess this preprocessing exception? This will trigger the associated webhook job."
                        onConfirm={() => handleReprocess(item.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          type="primary"
                          size="small"
                          icon={<ReloadOutlined />}
                          loading={reprocessingIds.has(item.id)}
                          disabled={reprocessingIds.has(item.id)}
                        >
                          Reprocess
                        </Button>
                      </Popconfirm>
                    );
                  }
                  return null;
                }}
                onClick={() => onRowClick(item)}
              >
                <Col 
                  {...description} 
                  data-label="Description"
                  title={item?.description || "==//=="}
            
                >
                  {item?.description || "-"}
                </Col>

                <Col 
                  {...council} 
                  data-label="Council"
                  title={item?.council_name || "==//=="}
                 
                  
                >
                  {item?.council_name || "-"}
                </Col>

                <Col 
                  {...reportType} 
                  data-label="Report Type"
                  title={item?.report_type || "==//=="}
               
                >
                  {item?.report_type?.toUpperCase() || "-"}
                </Col>

                <Col 
                  {...status}
                  className="contentEllipse"
                  data-label="Status"
                  style={statusColStyle}
                >
                  <span style={statusTagStyle(item)}>
                    {item?.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : "-"}
                  </span>
                </Col>

                <button 
                  className="view-card-details" 
                  onClick={() => onRowClick(item)}
                >
                  View Details
                </button>
              </ListItem>
            )}
          />
        </div>
      </Spin>
      
      <CustomPagination
        pagination={pagination}
        onPageChange={handlePageChange}
        loading={loading}
      />
      
      <ExceptionDrawer
        visible={drawerVisible}
        onClose={closeDrawer}
        exception={selectedException}
        onResolved={handleResolved}
      />
    </div>
  );
};

export default ExceptionPage;
